import React, { useEffect, useState } from "react";
import Footer from "../../Components/Footer/footer";
import SimpleButton from "../../Components/layouts/simple_button";
import { useNavigate, useParams } from "react-router";
import { TestCounter } from "../test_counter";
import TestResults from "../test_results";
import performRequest from "../../api/request";
import { useSelector } from "react-redux";
import { Toast } from "../../CustomComponent/toast";
import { CustomErrorResponse } from "../../CustomComponent/custom_error_response";
import LoadingPage from "../../Components/layouts/loading";

const TraineeTest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const store = useSelector((store) => store?.login?.user);

  const SESSION_KEY = `internal_test_${id}`;
  const SUBMITTED_KEY = `internal_test_submitted_${id}`;
  const [data, setData] = useState();
  const [timeLeft, setTimeLeft] = useState(20 * 60);
  const [questionNum, setQuestionNum] = useState(1);
  const [checkAns, setCheckAns] = useState([]);
  const [check, setCheck] = useState("");
  const [answer, setAnswers] = useState([]);
  const [display, setDisplay] = useState({ display: "block" });
  const [result, setResult] = useState(false);
  const [totalMarks, setTotalMarks] = useState();
  const [testScheduleId, setTestScheduleId] = useState();

  /* ----------------------------------
     🚫 BLOCK ALL NAVIGATION (BACK/FORWARD)
  -----------------------------------*/
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);

    const blockNavigation = () => {
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", blockNavigation);

    return () => window.removeEventListener("popstate", blockNavigation);
  }, []);

  /* ----------------------------------
     RESTORE STATE ON REFRESH
  -----------------------------------*/
  useEffect(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);

      setTimeLeft(parsed.timeLeft);
      setQuestionNum(parsed.questionNum);
      setCheckAns(parsed.checkAns || []);
      setAnswers(parsed.checkAns || []);
      setTestScheduleId(parsed.testScheduleId);

      const current = parsed.checkAns?.find(
        (a) => a.ques === parsed.questionNum
      );
      if (current) setCheck(current.ans);
    }
  }, [id]);

  /* ----------------------------------
     FETCH QUESTIONS
  -----------------------------------*/
  useEffect(() => {
    performRequest("POST", `/test/trainee-random-questions/${id}`)
      .then((res) => {
        setData(res.data.data);
        setTotalMarks(res?.data?.totalQuestions);
        setTestScheduleId(res?.data?.testScheduleId);
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          Toast("error", "No new tests available.");
          navigate("/my-scoreboard");
        } else if (err?.response?.status === 409) {
          Toast("error", "Test already attempted.");
          navigate("/my-scoreboard");
        } else if (err.response?.status === 410) {
          Toast("error", "Test time has expired.");
          navigate("/my-scoreboard");
        } else {
          CustomErrorResponse(err);
        }
      });
  }, [id]);

  /* ----------------------------------
     SAVE STATE CONTINUOUSLY
  -----------------------------------*/
  useEffect(() => {
    if (!data) return;

    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        timeLeft,
        questionNum,
        checkAns,
        testScheduleId,
        testId: id,
      })
    );
  }, [timeLeft, questionNum, checkAns, testScheduleId, data]);

  /* ----------------------------------
     KEEP RADIO SELECTION SYNCED
  -----------------------------------*/
  useEffect(() => {
    const current = checkAns.find((a) => a.ques === questionNum);
    setCheck(current ? current.ans : "");
  }, [questionNum, checkAns]);

  /* ----------------------------------
     AUTO SUBMIT ON TAB / BROWSER CLOSE
  -----------------------------------*/
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (localStorage.getItem(SUBMITTED_KEY)) return;

      navigator.sendBeacon(
        `${process.env.REACT_APP_API_URL}/test/auto-submit`,
        JSON.stringify({
          answers: checkAns,
          testScheduleId,
          testId: id,
        })
      );

      localStorage.setItem(SUBMITTED_KEY, "true");
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [checkAns, testScheduleId, id]);

  /* ----------------------------------
     AUTO SUBMIT ON TIMER END
  -----------------------------------*/
  useEffect(() => {
    if (timeLeft === 0 && !result) {
      handleTestSubmit(checkAns);
    }
  }, [timeLeft]);

  /* ----------------------------------
     FINAL SUBMIT
  -----------------------------------*/
  const handleTestSubmit = (ans) => {
    setAnswers(ans);
    setResult(true);

    sessionStorage.removeItem(SESSION_KEY);
    localStorage.setItem(SUBMITTED_KEY, "true");
  };

  /* ----------------------------------
     RESULT SCREEN
  -----------------------------------*/
  if (result) {
    return (
      <TestResults
        answer={answer}
        test={data}
        totalMarks={totalMarks}
        id={id}
        traineeTest={"traineeTest"}
      />
    );
  }

  if (!data) return <LoadingPage />;

  /* ----------------------------------
     TEST UI (CSS UNCHANGED)
  -----------------------------------*/
  return (
    <>
      <div className="page">
        <div className="page-wrapper">
          <div className="page-body">
            <div className="container-xxl">
              <div className="section-header">
                <h2 className="section-title section-title-lg m-0 p-0">
                  TRAINEE TEST
                </h2>
                <p className="section-description mb-4 p-0">
                  Do not refresh or logout. If you refresh or logout, your score
                  will be calculated as zero.
                </p>
              </div>

              <div className="row row-cards">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header text-center">
                      <TestCounter
                        timeLeft={timeLeft}
                        setTimeLeft={setTimeLeft}
                      />
                    </div>

                    <div className="card-body">
                      <div className="mx-4">
                        <ul className="steps steps-green my-2">
                          {data?.map((_, index) => (
                            <li
                              key={index}
                              className={`step-item ${
                                index + 1 === questionNum ? "active" : ""
                              }`}
                            >
                              {index + 1}
                            </li>
                          ))}
                        </ul>

                        <div className="card p-2">
                          {data
                            ?.filter((_, index) => index + 1 === questionNum)
                            ?.map((qus, index) => (
                              <div key={index}>
                                <h3 className="m-4 fs-2">
                                  {questionNum}. {qus.question}
                                </h3>

                                <div className="m-4">
                                  <div className="row">
                                    {["A", "B", "C", "D"].map((opt) => (
                                      <div
                                        key={opt}
                                        className="col-md-6 col-sm-12 mb-3"
                                      >
                                        <div className="card p-3 card-link card-link-pop h-100">
                                          <label className="form-check form-check-inline mb-0 fs-3 w-100">
                                            <input
                                              className="form-check-input me-2"
                                              type="radio"
                                              name="radios-inline"
                                              value={`option${opt}`}
                                              checked={check === `option${opt}`}
                                              onChange={(e) => {
                                                const value = e.target.value;
                                                let ans = [...checkAns];

                                                const existingIndex =
                                                  ans.findIndex(
                                                    (item) =>
                                                      item.ques === questionNum
                                                  );

                                                const newAnswer = {
                                                  ques: questionNum,
                                                  ans: value,
                                                  id: qus.id,
                                                };

                                                if (existingIndex >= 0) {
                                                  ans[existingIndex] =
                                                    newAnswer;
                                                } else {
                                                  ans.push(newAnswer);
                                                }

                                                setCheckAns(ans);
                                                setCheck(value);
                                              }}
                                            />
                                            <span className="form-check-label">
                                              {qus[`option${opt}`]}
                                            </span>
                                          </label>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                <hr className="m-2" />

                                <div className="text-center">
                                  <SimpleButton
                                    disabled={!check}
                                    buttonText={
                                      questionNum === data?.length
                                        ? "Submit Test"
                                        : "Submit & Next"
                                    }
                                    onClick={() => {
                                      if (questionNum === data?.length) {
                                        handleTestSubmit(checkAns);
                                      } else {
                                        setCheck("");
                                        setQuestionNum(questionNum + 1);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Footer />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TraineeTest;
